<script setup>
import {gameScoreText, scorePoints} from '~/utils/gameScore';
import {computed} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import TlIconBtn from "~/components/admin/TlIconBtn.vue";
import {useTimelineModels} from "@/composables/useTimelineModels";

// The americano rotation sheet: one compact row per match — "Anna + Ben vs
// Cara + Dan" with the score (or schedule state) on the right, in the visual
// language of fixtures/FixtureResultRow (winner bold, loser subtle). Rounds
// read as a slim chip on each row rather than headed groups, so the sheet sits
// comfortably beside the standings table — where the signed-in player's ROW is
// the only "you are here" marker; their matches are deliberately not tinted.
// Recording a result opens the in-page fixture dialog (?editingFixtureId,
// handled globally by BasePage), scheduling the in-page schedule dialog
// (?schedulingFixtureId, same mechanism), and live-capable unplayed matches
// get a live-scoring shortcut — the page refreshes on fixtureUpdated.
const props = defineProps({
  // The division's fixtures (FixtureListResponse rows + americanoRound).
  fixtures: {type: Array, required: true},
});

const route = useRoute();
const router = useRouter();
const {teamName} = useTimelineModels();
const {outputDateFromDb} = useCrudConverters();
const {isAuthenticated, user, isSuperUser, isAdminOfClub} = useAuthService();
const {
  isPlayerInFixture, teamIdFromPlayerInFixture, fixtureScheduleState, fixtureScheduleActionLabel,
} = useValidation();

const isPlayed = (f) => f?.homeTeamResult != null && f?.awayTeamResult != null;

// Rounds ascending, API order within — flat, with round boundaries marked by
// the chip on each row.
const ordered = computed(() => [...(props.fixtures || [])]
    .sort((a, b) => (a.americanoRound ?? 0) - (b.americanoRound ?? 0)));

const homeWon = (f) => isPlayed(f) && Number(f.homeTeamResult) > Number(f.awayTeamResult);
const awayWon = (f) => isPlayed(f) && Number(f.awayTeamResult) > Number(f.homeTeamResult);

// The signed-in player's matches get a quiet "you are here" accent so they
// can pick their games out of the sheet at a glance.
const isMine = (fixture) =>
    isAuthenticated.value && isPlayerInFixture(user.value?.playerId, fixture);

// Same edit gate as FixtureCell: club admins and the players in the match.
const canEdit = (fixture) => {
  if (!isAuthenticated.value) return false;
  if (isSuperUser.value || isAdminOfClub(fixture.clubId)) return true;
  return isPlayerInFixture(user.value?.playerId, fixture);
};

// In-page result entry — BasePage watches ?editingFixtureId and opens the
// fixture dialog; saving fires fixtureUpdated, which the page listens for.
const openResultDialog = (fixture) => {
  router.push({...route, query: {...route.query, editingFixtureId: fixture.id}});
};

// Per-game scores in play order, e.g. "11-7 · 11-9" for the score tooltip.
const gamesLine = (fixture) => (fixture?.games || [])
    .slice()
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    .map(g => gameScoreText(g.homeTeamScore, g.awayTeamScore, '-'))
    .join(' · ');

// Played but not yet approved by both pairs — feeds/standings only count
// fully approved matches, so surface the pending state.
const awaitingApproval = (f) =>
    isPlayed(f) && (f.homeTeamApprovedAt == null || f.awayTeamApprovedAt == null);

// Chip mirrors FixtureCell's schedule reading: a confirmed date, a proposed
// (one-sided) date, or nothing yet. Proposed dates surface so the pair can see
// a suggestion is on the table without opening the match.
const proposedAt = (f) => f?.homeTeamScheduledAt || f?.awayTeamScheduledAt;
const scheduleLabel = (fixture) => {
  const date = fixture?.playedAt || fixture?.scheduledAt || proposedAt(fixture);
  return date ? outputDateFromDb(date, 'd MMM') : 'To play';
};
const scheduleIcon = (fixture) => {
  if (fixture?.scheduledAt) return 'mdi-calendar-check';
  if (proposedAt(fixture)) return 'mdi-calendar-clock';
  return 'mdi-calendar-blank-outline';
};

// Chip tooltip: the full when-and-where — day, date, time and the court when
// one is booked, e.g. "Tuesday 12 August, 09:30 · Court 1".
const scheduleChipTooltip = (fixture) => {
  const date = fixture?.scheduledAt || proposedAt(fixture);
  if (!date) return null;
  const when = outputDateFromDb(date, 'EEEE d MMMM, HH:mm');
  const court = fixture?.playingAreaName ? ` · ${fixture.playingAreaName}` : '';
  return `${fixture?.scheduledAt ? '' : 'Suggested: '}${when}${court}`;
};

// Same propose/respond/reschedule wording as FixtureCell — the shared schedule
// state machine in useValidation, keyed off the viewer's pair team.
const scheduleTooltip = (fixture) => {
  const myTeamId = teamIdFromPlayerInFixture(user.value?.playerId, fixture);
  return fixtureScheduleActionLabel(fixtureScheduleState(fixture, myTeamId));
};

// In-page scheduling — BasePage watches ?schedulingFixtureId, exactly like
// result entry; saving fires fixtureUpdated and the page refreshes.
const openScheduleDialog = (fixture) => {
  router.push({...route, query: {...route.query, schedulingFixtureId: fixture.id}});
};
</script>

<template>
  <div class="arl">
    <router-link
        v-for="fixture in ordered"
        :key="fixture.id"
        class="arl-row"
        :class="{ 'arl-row--mine': isMine(fixture) }"
        :to="`/fixtures/${fixture.id}`"
    >
      <span class="arl-round">R{{ fixture.americanoRound }}</span>

      <div class="arl-pairs">
        <span class="arl-pair" :class="{ 'arl-pair--lost': awayWon(fixture) }">{{ teamName(fixture.homeTeam) }}</span>
        <span class="arl-vs">vs</span>
        <span class="arl-pair" :class="{ 'arl-pair--lost': homeWon(fixture) }">{{ teamName(fixture.awayTeam) }}</span>
      </div>

      <template v-if="isPlayed(fixture)">
        <v-icon v-if="awaitingApproval(fixture)" size="14" class="arl-pending" icon="mdi-clock-outline">
          <v-tooltip activator="parent" location="top">Awaiting approval</v-tooltip>
        </v-icon>
        <span class="arl-score">
          {{ fixture.homeTeamResult }}–{{ fixture.awayTeamResult }}
          <v-tooltip v-if="gamesLine(fixture)" activator="parent" location="top">{{ gamesLine(fixture) }}</v-tooltip>
        </span>
      </template>
      <span v-else class="tl-chip arl-when">
        <v-icon size="13" :icon="scheduleIcon(fixture)"/>
        {{ scheduleLabel(fixture) }}
        <v-tooltip
            v-if="scheduleChipTooltip(fixture)"
            activator="parent"
            location="top"
        >{{ scheduleChipTooltip(fixture) }}</v-tooltip>
      </span>

      <span v-if="canEdit(fixture)" class="arl-actions" @click.stop.prevent>
        <tl-icon-btn
            v-if="!isPlayed(fixture)"
            icon="mdi-calendar-clock"
            :tooltip="scheduleTooltip(fixture)"
            @click="openScheduleDialog(fixture)"
        />
        <tl-icon-btn
            v-if="!isPlayed(fixture) && fixture.supportsLiveScoring"
            icon="mdi-scoreboard-outline"
            tooltip="Live score"
            @click="router.push(`/fixtures/${fixture.id}/live`)"
        />
        <tl-icon-btn
            :icon="isPlayed(fixture) ? 'mdi-pencil' : 'mdi-plus-circle-outline'"
            :tooltip="isPlayed(fixture) ? 'Edit result' : 'Record result'"
            @click="openResultDialog(fixture)"
        />
      </span>
    </router-link>
  </div>
</template>

<style scoped lang="scss">
.arl {
  display: flex;
  flex-direction: column;
}

.arl-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  border-left: 3px solid transparent;
  text-decoration: none;
  color: rgb(var(--v-theme-text));

  /* your matches: a quiet left accent + tint, not a shout */
  &--mine {
    border-left-color: rgb(var(--v-theme-primary));
    background: rgba(var(--v-theme-primary), 0.05);
  }

  & + .arl-row {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }

  &:hover {
    background: rgb(var(--v-theme-light));
  }
}

/* slim per-row round marker in the americano purple pair */
.arl-round {
  flex-shrink: 0;
  min-width: 26px;
  text-align: center;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.03em;
  padding: 2px 4px;
  border-radius: 6px;
  background: #F0EAFB;
  color: #7C3AED;
}

.arl-pairs {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.arl-pair {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* loser: subtle ink + lighter weight (FixtureResultRow .fxr-team--lost) */
  &--lost {
    color: rgb(var(--v-theme-textSubtle));
    font-weight: 500;
  }
}

.arl-vs {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));
  flex-shrink: 0;
}

.arl-score {
  font-size: 13.5px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  flex-shrink: 0;
}

.arl-pending {
  color: rgb(var(--v-theme-textSubtle));
  flex-shrink: 0;
}

.arl-when {
  flex-shrink: 0;
}

.arl-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}
</style>
